/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import flatten from 'lodash.flatten';
import flow from 'lodash.flow';
import remove from 'lodash.remove';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { RootStore } from 'src/core/stores/RootStore';
import { BaseEventParams } from 'src/core/types/eventBusEvent';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getIntentService } from 'src/luis/core/services/intents/intentService';
import { IntentSwagger, LabelsPerIntentSwagger } from 'src/luis/core/services/intents/intentSwagger';
import { createIntentStore } from 'src/luis/core/stores/intentStore';
import { addGlobalFeaturesToNewModels } from 'src/luis/core/utils/addGlobalFeaturesToNewModels';
import { intentSwaggerToIntentStore } from 'src/luis/modules/intents/intentMappers';
import { LuisId } from 'src/luis/types/Resource';
import { DomainModel } from 'src/luis/types/stores/domains/DomainStore';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';
import { IntentStoreHouse } from 'src/luis/types/stores/intents/IntentStoreHouse';

export type IntentHandlerGroup = ReturnType<typeof createIntentHandlerGroup>;

export type CreateIntentEvent = BaseEventParams<object, { intentStore: IntentStore }>;

/**
 * Re-fetches the intent label count and assigns the correct
 *
 * @param intentService The intent service responsible for calling the api.
 * @param intentStoreHouse The intent store house that holds the data.
 */
const refreshIntentsLabelCount = async (
    intentService: ReturnType<typeof getIntentService>,
    intentStoreHouse: IntentStoreHouse
): Promise<void> => {
    const labelsPerIntentCount = await intentService.getLabelsPerIntentCount();

    const newIntentStores = intentStoreHouse.items.map<IntentStore>(intentStore =>
        createIntentStore(intentStore.id, {
            name: intentStore.name,
            labelsCount: labelsPerIntentCount[intentStore.id],
            featureStoreHouse: intentStore.featureStoreHouse
        })
    );

    intentStoreHouse.setItems(newIntentStores);
};

/**
 * Loads the user created intents.
 */
export const loadIntentsHelper = (rootStore: RootStore) => async (params?: HandlerParams) => {
    const { context } = params;
    const { appId, versionId, forceRefresh } = context;
    const logger = getLogger();

    const intentService = getIntentService({ context });
    const { intentStoreHouse } = rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);

    if (!forceRefresh && (intentStoreHouse.isLoading || intentStoreHouse.isLoaded)) {
        return;
    }

    intentStoreHouse.setIsLoading(true);

    const logIntentTimedActivity = logger.createTimedActivityLogger<IntentSwagger[]>('LoadIntents');
    const logIntentLabelsTimedActivity = logger.createTimedActivityLogger<LabelsPerIntentSwagger>('LoadLabelsPerIntent');

    const intentSwaggers = await logIntentTimedActivity(intentService.getIntents());
    const labelsPerIntentSwagger = await logIntentLabelsTimedActivity(intentService.getLabelsPerIntentCount());

    const intentStores = intentSwaggers.map(intent => intentSwaggerToIntentStore(intent, labelsPerIntentSwagger[intent.id]));

    intentStoreHouse.setItems(intentStores);
    intentStoreHouse.setIsLoaded(true);
    intentStoreHouse.setIsLoading(false);
};

/**
 * Represents a handler group for intent operations.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The handler group dependencies.
 */
export const createIntentHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const eventBus = getEventBusV2();
    const logger = getLogger();

    /**
     * Loads the user created intents.
     */
    const loadIntents = loadIntentsHelper(rootStore);

    /**
     * Creates a new intent.
     *
     * @param param0.name The name of the intent to create.
     */
    const createIntent = async ({ name, context, tracker }: HandlerParams<Pick<IntentStore, 'name'>>) =>
        flow(async () => {
            const eventId = getId();
            const { appId, versionId } = context;
            const intentService = getIntentService({ context });
            const appVersionStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);
            const logTimedActivity = logger.createTimedActivityLogger<string>('CreateIntent', () => ({ name }));

            try {
                eventBus.emit<CreateIntentEvent>({ kind: 'createIntent', id: eventId, eventType: 'running' });

                const intentId = await logTimedActivity(intentService.createIntent({ name }));
                const intentStore = createIntentStore(intentId, { name });

                addGlobalFeaturesToNewModels(intentStore, appId, versionId, rootStore);
                appVersionStore.intentStoreHouse.add(intentStore);

                tracker.setContext(intentId);
                eventBus.emit<CreateIntentEvent>({ kind: 'createIntent', id: eventId, eventType: 'completed', intentStore });
            } catch (e) {
                eventBus.emit<CreateIntentEvent>({ kind: 'createIntent', id: eventId, eventType: 'failed', error: <Error>e });
                throw e;
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Renames the intent with the given id.
     */
    const renameIntent = async ({ id, name, context }: HandlerParams<Pick<IntentStore, 'id' | 'name'>>) => {
        const { appId, versionId } = context;
        const intentService = getIntentService({ context });
        const appVersionStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);
        const logTimedActivity = logger.createTimedActivityLogger('RenameIntent', () => ({ id, name }));

        await logTimedActivity(intentService.renameIntent({ id: <string>id, name }));

        const intentStore = appVersionStore.intentStoreHouse.findById(id);

        intentStore.rename(name);
    };

    /**
     * Deletes intents with the given ids.
     *
     * @param param0.ids The intent ids to delete.
     */
    const deleteIntents = async ({ ids, deleteReferences, context }: HandlerParams<{ ids: LuisId[]; deleteReferences: boolean }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const {
                intentStoreHouse,
                patternStoreHouse,
                predictionStoreHouse: { predictions }
            } = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);
            const eventId = getId();

            const utterancesText = flatten(ids.map(id => intentStoreHouse.findById(id).utteranceStoreHouse.allPages.map(u => u.text)));

            const intentService = getIntentService({ context });
            const servicePromises = ids.map(id => intentService.deleteIntent({ id: <string>id, deleteUtterances: deleteReferences }));
            const logTimedActivity = logger.createTimedActivityLogger<void[]>('DeleteIntents', () => ({ ids: JSON.stringify(ids) }));

            try {
                eventBus.emit<BaseEventParams>({ kind: 'deleteIntents', id: eventId, eventType: 'running' });

                await logTimedActivity(Promise.all(servicePromises));

                intentStoreHouse.batchRemoveByIds(ids);

                eventBus.emit<BaseEventParams>({ kind: 'deleteIntents', id: eventId, eventType: 'completed' });
            } catch (e) {
                eventBus.emit<BaseEventParams>({ kind: 'deleteIntents', id: eventId, eventType: 'failed', error: <Error>e });
                throw e;
            }

            // Deletes the dangling pointers for the patterns that is labeled with these intents.
            const referencedPatterns = flatten(ids.map(id => [...patternStoreHouse.items.filter(p => p.intentId === id)]));

            // If you choose to keep the intents, refresh the intent label count for the None intent
            // and update the referenced labeled pattern.
            if (!deleteReferences) {
                const noneIntentId = intentStoreHouse.findByName(LuisConstants.Luis.NoneIntentName).id;
                referencedPatterns.forEach(p => p.setIntentId(<string>noneIntentId));

                await refreshIntentsLabelCount(intentService, intentStoreHouse);
            } else {
                utterancesText.forEach(uT => predictions.delete(uT));
                patternStoreHouse.batchRemoveByIds(referencedPatterns.map(p => p.id));
            }

            // Deletes dangling pointers in the predictions
            predictions.forEach(p => remove(p.predictedIntents, pi => ids.includes(pi.intentId)));
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Add a prebuilt domain intents to the current app.
     *
     * @param param0.prebuiltDomains The prebuilt domain intents to be added.
     */
    const addPrebuiltIntents = async ({ prebuiltDomains, context }: HandlerParams<{ prebuiltDomains: DomainModel[] }>) => {
        const intentService = getIntentService({ context });
        const { intentStoreHouse } = rootStore.currentLuisAppStore.currentAppVersionStore;
        const logTimedActivity = logger.createTimedActivityLogger<string[]>('AddPrebuiltIntents', () => ({
            ids: JSON.stringify(prebuiltDomains.map(name => name))
        }));

        const servicePromises = intentService.addPrebuiltDomainIntents(prebuiltDomains);
        const intentIds = await logTimedActivity(servicePromises);
        const intentStores = prebuiltDomains.map((domain, index) => createIntentStore(intentIds[index], { name: domain.name }));

        intentStoreHouse.batchAdd(intentStores);

        await refreshIntentsLabelCount(intentService, intentStoreHouse);
    };

    return { loadIntents, createIntent, renameIntent, deleteIntents, addPrebuiltIntents };
};
